import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../lib/animations';
import RollGameTab from '../components/games/RollGameTab';
import RealisticRollTab from '../components/games/RealisticRollTab';
import GlobalPassTab from '../components/games/GlobalPassTab';
import StrainTriviaTab from '../components/games/StrainTriviaTab';
import LegalOrNotTab from '../components/games/LegalOrNotTab';

const GAME_TABS = [
  { key: 'roll', label: 'Roll & Smoke', desc: 'Build and roll your perfect virtual joint' },
  { key: 'realistic', label: 'Realistic Roll', desc: 'Break, spread, tuck, roll, seal — hands-on' },
  { key: 'pass', label: 'Global Pass', desc: 'See joints being passed around the world' },
  { key: 'trivia', label: 'Strain Trivia', desc: 'Test your cannabis knowledge' },
  { key: 'legal', label: 'Legal or Not?', desc: 'Guess cannabis legality by country' },
  { key: 'coming', label: 'More Games', desc: 'Coming soon' },
] as const;

type GameTab = (typeof GAME_TABS)[number]['key'];

const COMING_SOON = [
  { name: 'Guess the Strain', desc: 'Identify strains by their effects and flavors.' },
  { name: 'Price Is Right', desc: 'Guess cannabis prices around the world.' },
  { name: 'Terpene Match', desc: 'Match terpenes to aromas and effects.' },
];

export default function Games() {
  const [activeTab, setActiveTab] = useState<GameTab>('roll');

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <h2 className="text-2xl font-bold text-cream">Games</h2>

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {GAME_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-5 py-2.5 text-sm font-semibold rounded-xl whitespace-nowrap transition-colors ${
              activeTab === tab.key ? 'text-[#4ade80]' : 'text-white/40 hover:text-white/60'
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <motion.div
                layoutId="game-tab-indicator"
                className="absolute inset-0 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-xl -z-10"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'roll' && <RollGameTab />}
          {activeTab === 'realistic' && <RealisticRollTab />}
          {activeTab === 'pass' && <GlobalPassTab />}
          {activeTab === 'trivia' && <StrainTriviaTab />}
          {activeTab === 'legal' && <LegalOrNotTab />}
          {activeTab === 'coming' && (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {COMING_SOON.map(game => (
                <motion.div
                  key={game.name}
                  variants={staggerItem}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-2"
                >
                  <h3 className="text-white font-semibold text-sm">{game.name}</h3>
                  <p className="text-white/40 text-xs">{game.desc}</p>
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-white/5 text-white/30 border border-white/10">
                    Coming Soon
                  </span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
